import { FormControl, InputLabel, OutlinedInput, OutlinedInputProps } from '@mui/material';
import * as R from 'ramda';
import React, { ChangeEvent, useEffect, useId } from 'react';
import { IMask, useIMask } from 'react-imask';

export interface TimeInputProps extends Omit<OutlinedInputProps, 'onChange'> {
  onChange: (event: ChangeEvent<HTMLInputElement> | null, value: string) => void;
  maskOptions?: IMask.AnyMaskedOptions;
}

export const defaultMaskOptions = {
  mask: 'HH:mm',
  overwrite: true,
  blocks: {
    HH: {
      mask: IMask.MaskedRange,
      from: 0,
      to: 23,
    },
    mm: {
      mask: IMask.MaskedRange,
      from: 0,
      to: 59,
    },
  },
};

export const TimeInput: React.FC<TimeInputProps> = ({
  maskOptions = defaultMaskOptions,
  onChange,
  className,
  value: defaultValue,
  ...props
}) => {
  const id = useId();

  const { ref: inputRef, setValue } = useIMask(R.mergeDeepRight(defaultMaskOptions, maskOptions), {
    onComplete: (value) => {
      if (value !== defaultValue) {
        onChange(null, value);
      }
    },
  });

  useEffect(() => {
    setValue(defaultValue as string);
  }, [defaultValue, setValue]);

  return (
    <FormControl className="w-full">
      {props.label && (
        <InputLabel shrink htmlFor={id}>
          {props.label}
        </InputLabel>
      )}
      <OutlinedInput
        defaultValue={defaultValue}
        id={id}
        inputRef={inputRef}
        /* NOTE: There's some weird padding issue (`padding: 16.5px 14px`) with
         * the input component, so we need to override it here */
        inputProps={{ className: 'px-3 py-2' }}
        className={`${className} rounded-none border-grey-300 border-x-0`}
        {...props}
      />
    </FormControl>
  );
};
